import {
  AcceptPromotionRequest,
  AcceptPromotionResponse,
  CreatePaymentMethodArgs as ApiCreatePaymentMethodArgs,
  Coupon,
  Customer,
  ExternalPaymentPortalLinkRequest,
  ExternalPaymentPortalLinkResponse,
  GetAllPlansArgs,
  GetPromotionArgs,
  PodcastPromoCreditsResponse,
  ProductSubTier,
  Promotion,
  ReferralOwner,
  SendReferralEmailInvitationsArgs,
  SubscriptionPlanDetail,
  SubscriptionPlanName,
  SubscriptionUpdateRequest,
  UserSubscription,
} from '@sparemin/api-sdk';

import { IApiAction, IApiResponse, NormalizationResult } from '../types';

export const ACTION_KEY = 'PLAN_SERVICE' as const;

export enum ServiceMethod {
  CREATE_PAYMENT_METHOD = 'CREATE_PAYMENT_METHOD',
  GET_CUSTOMER = 'GET_CUSTOMER',
  GET_MY_COUPONS = 'GET_MY_COUPONS',
  GET_MY_REFERRAL = 'GET_MY_REFERRAL',
  GET_MY_PODCAST_PROMO_CREDITS = 'GET_MY_PODCAST_PROMO_CREDITS',
  GET_MY_SUBSCRIPTION = 'GET_MY_SUBSCRIPTION',
  GET_PLANS = 'GET_PLANS',
  SEND_REFERRAL_INVITATIONS = 'SEND_REFERRAL_INVITATIONS',
  UPDATE_MY_SUBSCRIPTION = 'UPDATE_MY_SUBSCRIPTION',
  GET_EXTERNAL_PORTAL_LINK = 'GET_EXTERNAL_PORTAL_LINK',
  GET_PROMOTION = 'GET_PROMOTION',
  ACCEPT_PROMOTION = 'ACCEPT_PROMOTION',
}

export const PRO_TIERS = [
  ProductSubTier.BASIC_20200922_PAID,
  ProductSubTier.BASIC_20211112_PAID,
  ProductSubTier.BASIC_20250516_PAID,
  ProductSubTier.BASIC_20250818_PAID,
  ProductSubTier.ENTERPRISE,
  ProductSubTier.ENTERPRISE_API_INTEGRATOR,
  ProductSubTier.PRO_20200922_PAID,
  ProductSubTier.PRO_20211112_PAID,
  ProductSubTier.PRO_20250516_PAID,
  ProductSubTier.PRO_20250818_PAID,
  ProductSubTier.PRO_GROW_20261120_PAID,
  ProductSubTier.PRO_ADMIN_ASSIGNED,
  ProductSubTier.PRO_FREE_TEMPORARY,
  ProductSubTier.PRO_EDUCATION_CHARITY,
  ProductSubTier.PRO_PAID,
  ProductSubTier.UNLIMITED_20211112_PAID,
  ProductSubTier.UNLIMITED_ADMIN_ASSIGNED,
  ProductSubTier.PRO_PAUSED,
  ProductSubTier.BASIC_PAUSED,
];

type NormalizedPlans = {
  entities: {
    plans: {
      [Name in SubscriptionPlanName]: SubscriptionPlanDetail;
    };
    result: SubscriptionPlanName[];
  };
};

export type GetPlansArgs = GetAllPlansArgs;
export type GetPlanResult = NormalizedPlans;

export type GetMySubscriptionArgs = undefined;
export type GetMySubscriptionResult = UserSubscription;

export type UpdateMySubscriptionArgs = Pick<
  SubscriptionUpdateRequest,
  | 'subscribeTo'
  | 'autoRenewal'
  | 'forcedCancellationNotice'
  | 'pausePlanDurationMonths'
  | 'cancelPause'
  | 'rewardfulReferral'
>;
export type UpdateMySubscriptionResult = void;

export type CreatePaymentMethodArgs = Pick<
  ApiCreatePaymentMethodArgs,
  'paymentMethodId' | 'stripeToken'
>;
export type CreatePaymentMethodResult = void;

export type GetCustomerArgs = undefined;
export type GetCustomerResult = Customer;

export type GetMyCouponsArgs = undefined;

export type GetMyReferralArgs = undefined;
export type GetMyReferralResult = ReferralOwner;

export type GetMyPodcastPromoCreditsResult = PodcastPromoCreditsResponse;

export type SendReferralInvitationsArgs = SendReferralEmailInvitationsArgs;
export type SendReferralInvitationResult = void;

export type GetExternalPortalLinkArgs = ExternalPaymentPortalLinkRequest;
export type GetExternalPortalLinkResult = ExternalPaymentPortalLinkResponse;

export interface GetMyCouponsResult {
  coupons: Coupon[];
}

export type AcceptPromotionArgs = Omit<AcceptPromotionRequest, 'isNewUser'>;
export type AcceptPromotionResult = AcceptPromotionResponse;

export type GetPromotionResult = NormalizationResult<
  'promotionKey',
  string,
  Promotion
>;

type ApiAction<M extends ServiceMethod, A> = IApiAction<
  typeof ACTION_KEY,
  M,
  A
>;
export type GetPlansAction = ApiAction<ServiceMethod.GET_PLANS, GetPlansArgs>;
export type GetMyPlanAction = ApiAction<
  ServiceMethod.GET_MY_SUBSCRIPTION,
  GetMySubscriptionArgs
>;
export type GetMySubscriptionAction = ApiAction<
  ServiceMethod.GET_MY_SUBSCRIPTION,
  GetMySubscriptionArgs
>;
export type UpdateMySubscriptionAction = ApiAction<
  ServiceMethod.UPDATE_MY_SUBSCRIPTION,
  UpdateMySubscriptionArgs
>;
export type CreatePaymentMethodAction = ApiAction<
  ServiceMethod.CREATE_PAYMENT_METHOD,
  CreatePaymentMethodArgs
>;
export type GetCustomerAction = ApiAction<
  ServiceMethod.GET_CUSTOMER,
  GetCustomerArgs
>;
export type GetMyCouponsAction = ApiAction<
  ServiceMethod.GET_MY_COUPONS,
  GetMyCouponsArgs
>;
export type GetMyReferralAction = ApiAction<
  ServiceMethod.GET_MY_REFERRAL,
  GetMyReferralArgs
>;
export type GetMyPodcastPromoCreditsAction = ApiAction<
  ServiceMethod.GET_MY_PODCAST_PROMO_CREDITS,
  undefined
>;
export type SendReferralInvitationsAction = ApiAction<
  ServiceMethod.SEND_REFERRAL_INVITATIONS,
  SendReferralInvitationsArgs
>;
export type GetExternalPortalLinkAction = ApiAction<
  ServiceMethod.GET_EXTERNAL_PORTAL_LINK,
  GetExternalPortalLinkArgs
>;

export type GetPromotionAction = ApiAction<
  ServiceMethod.GET_PROMOTION,
  GetPromotionArgs
>;

export type AcceptPromotionAction = ApiAction<
  ServiceMethod.ACCEPT_PROMOTION,
  AcceptPromotionArgs
>;

export interface PlanServiceDispatch {
  (action: GetPlansAction): Promise<IApiResponse<GetPlanResult>>;
  (action: GetMySubscriptionAction): Promise<
    IApiResponse<GetMySubscriptionResult>
  >;
  (action: UpdateMySubscriptionAction): Promise<
    IApiResponse<UpdateMySubscriptionResult>
  >;
  (action: CreatePaymentMethodAction): Promise<
    IApiResponse<CreatePaymentMethodResult>
  >;
  (action: GetCustomerAction): Promise<IApiResponse<GetCustomerResult>>;
  (action: GetMyCouponsAction): Promise<IApiResponse<GetMyCouponsResult>>;
  (action: GetMyReferralAction): Promise<IApiResponse<GetMyReferralResult>>;
  (action: GetMyPodcastPromoCreditsAction): Promise<
    IApiResponse<GetMyPodcastPromoCreditsResult>
  >;
  (action: SendReferralInvitationsAction): Promise<
    IApiResponse<SendReferralInvitationResult>
  >;
  (action: GetExternalPortalLinkAction): Promise<
    IApiResponse<GetExternalPortalLinkResult>
  >;
  (action: GetPromotionAction): Promise<IApiResponse<GetPromotionResult>>;
  (action: AcceptPromotionAction): Promise<IApiResponse<AcceptPromotionResult>>;
}
